// ---------------------------------------------------------------------------
// Feature-Flag-Registry ("Feature-Flag-Buch")
// ---------------------------------------------------------------------------
// Single source of truth for UI features that are intentionally hidden behind a
// flag — work-in-progress, pending data validation, or a staged rollout. Every
// entry is documented so it is always clear WHAT is gated, WHY it is hidden,
// SINCE when and who owns the decision. The human-readable overview lives in
// FEATURE_FLAGS.md at the repository root; keep both in sync.
//
// Lifecycle: when a feature is permanently released, flip nothing — instead
// remove its entry here, delete the gate at the call site, and strike the row
// in FEATURE_FLAGS.md (move it to the "Retired" section with the release date).
//
// Every entry carries a `stage` — the ONE place a feature's state is decided:
//
//   'live'      On everywhere. Its changelog items may sit in a published release.
//   'upcoming'  Hard off on master/live, on in staging. The feature is announced:
//               its items stay in the in-development changelog entry and may
//               tease staff under "Demnächst verfügbar".
//   'secret'    Hard off on master/live, on in staging, AND not announced at all.
//               While the flag is off, nothing about it may appear anywhere a
//               live user can read — no changelog item, no teaser, no guide, no
//               title. Currently: the AI phone assistant and its evaluation.
//   'stagingOnly' Hard off on master/live, on in staging, and not announced either.
//               The state for work that is simply NOT FINISHED: the code is
//               merged to master so both branches stay in step, and the switch
//               keeps it from taking effect in production. Unlike 'secret' it
//               carries no product decision — nobody chose to keep it quiet, it
//               is just not done — and it needs no changelog item at all until
//               the work is ready. This is what makes "only on staging" cheaper
//               than not merging, which is how the branches drifted apart.
//   'postponed' Hard off EVERYWHERE, staging included, and not announced either.
//               The project behind it is on hold ("verschoben"): the code stays
//               in the repository, but there is nothing to review, so it comes
//               off the test environment as well. Currently: the chat agent.
//
// 'upcoming' and 'secret' are fully usable on staging — between those two the
// difference is only what may be LEARNED about them on live, never what the code
// does. 'postponed' is the one state that also changes what staging shows. Keep
// `stage` in sync with the same registry in funzone-management-frontend-new,
// with FEATURE_FLAG_STAGES in the backend's util/changelog-feature-flags.js,
// and with FEATURE_FLAGS.md. All four together or none.
//
// Resolution order for a flag:
//   1. An explicit Vite env var `VITE_FEATURE_<UPPER_SNAKE_KEY>` set to the
//      string 'true' or 'false' always wins (e.g. VITE_FEATURE_QR_TICKETS).
//   2. A postponed flag is OFF — staging does not bring it back, because there
//      is nothing to review while its project is on hold.
//   3. On STAGING every other registered flag is ON, so anything gated can be
//      reviewed there without ever being exposed on live.
//   4. Otherwise `stage === 'live'` decides — which is how a flag stays hidden
//      on live/master.
// Staging is recognised by VITE_IS_STAGING=true (set in .env.staging, used by
// `npm run build:staging`).
//
// Where a flag becomes visible is part of its entry, so the UI can point the
// notice at the page it belongs to instead of showing one permanent bar:
//   • `pageRoutes`    — routes that exist or render ONLY because the flag is on.
//                       Whoever sees them is looking at something that is not
//                       there on live.
//   • `partialRoutes` — pages that exist on live too, but carry a gated part
//                       (a section, a tab, an extra action).
// Sub-paths are covered automatically ('/airkey' also matches
// '/airkey/mappings'). Surfaces without a route of their own — a button inside
// the booking-details modal, a global keyboard/scanner listener — stay empty on
// purpose: they are reachable from nearly every view, and listing them per route
// would put the bar back on every page. They remain documented in `note` and in
// the environment chip's tooltip.

export const FEATURE_FLAGS = {
  callProtocol: {
    label: 'Gesprächsprotokolle',
    description:
      'Protokoll zu jedem eingehenden Anruf — öffnet sich automatisch, sobald das Telefon beim Mitarbeiter klingelt, und ist bei einer Übergabe durch den KI-Agenten vorbefüllt.',
    stage: 'upcoming',
    since: '2026-08-10',
    owner: 'max@laserzone.de',
    // The list view only exists behind the flag; the call-center statistic keeps
    // its other sections on live and only adds the protocol section here. The
    // dialog that opens on a ringing phone has no route of its own.
    pageRoutes: ['/callProtocols'],
    partialRoutes: ['/callCenterStats'],
    note: 'Vorerst nur auf Staging, bis der Ablauf mit dem Call Center abgestimmt ist. Das Backend blockt zusätzlich alle /api/v1/callProtocol/*-Endpoints mit 503 CALL_PROTOCOL_DISABLED und legt beim Comdesk-Event ringing_outbound kein Protokoll an, solange CALL_PROTOCOL_ENABLED dort nicht greift — beide Seiten müssen zum Aktivieren gesetzt sein. Rechte: callProtocol:read (eigene), callProtocol:read:all (alle Mitarbeiter), callProtocol:write.',
  },
  pagerBriefing: {
    label: 'Pager-Ruf zum Briefing',
    description:
      'LRS-Coaster-Pager für Gäste — Zuweisung beim Check-in über einen Kartenauswahl-Dialog in der Briefing-Ansicht und der Ruf "Rufen", der den Coaster der Gruppe funken lässt.',
    stage: 'secret',
    since: '2026-09-04',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: [],
    note: 'Ohne Oberfläche in diesem Dashboard — das Feature ist nach dem Feature-Freeze nur im führenden funzone-management-frontend-new gebaut. Der Eintrag steht hier, weil der Zustand eines Flags an allen vier Stellen zusammen gepflegt wird. Geheim: darf live in keinem Changelog, Guide oder Teaser auftauchen. Das Backend gated die gesamte /api/v1/pager-API mit PAGER_BRIEFING_ENABLED (503 PAGER_BRIEFING_DISABLED).',
  },
  voiceAi: {
    label: 'KI-Telefonie',
    description:
      'Auswertung des KI-Agenten am Telefon — Abschnitt "KI-Telefonie" in der Call-Center-Statistik (KPI-Karten, Tagesverlauf, Volumenaufteilung, Gesprächsausgänge, Themen).',
    stage: 'secret',
    since: '2026-08-09',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: ['/callCenterStats'],
    note: 'Vorerst nur auf Staging, bis der Post-Call-Sync gegen die echte ElevenLabs-API verifiziert ist — das Feld-Mapping konnte nicht gegen einen echten Response geprüft werden. Das Backend liefert den `ai`-Block nur bei gesetztem VOICE_AI_ENABLED und lässt den Sync-Cron sonst aus. Achtung: Das Flag betrifft ausschliesslich die Auswertung — die Voice-Tool-Endpunkte (/api/v1/agent/voice/*, MCP) laufen weiter, der produktive KI-Agent wird davon nicht abgeschaltet.',
  },
  agentChat: {
    label: 'KI-Chat („FunZone Assistent")',
    description:
      'Chat-Agent — Menüeintrag "Eskalierte Chats" samt Route /escalated-chats und die zugehörigen Chat-Endpunkte.',
    stage: 'postponed',
    since: '2026-08-09',
    owner: 'max@laserzone.de',
    pageRoutes: ['/escalated-chats'],
    partialRoutes: [],
    note: 'Das Projekt ist verschoben, deshalb "postponed" statt "secret": der Chat ist auch auf Staging aus, weil es nichts zu begutachten gibt, solange er auf Eis liegt. Er soll laut Planung durch ElevenLabs ersetzt werden; das Flag nimmt ihn aus der Oberfläche, bevor der Code entfernt wird. Das Backend blockt die Chat-Endpunkte (öffentliches Widget, /agent/message, /agent/session, Eskalation) mit 503 AGENT_CHAT_DISABLED, jetzt auch auf Staging (agent_chat_enabled hängt dort an postponedFeatureFlagEnabled); AGENT_CHAT_ENABLED=true bzw. VITE_FEATURE_AGENT_CHAT=true holt ihn je Umgebung zurück. Nicht betroffen: Mitarbeiter-Chat (eigenes Feature), Agenten-Chat-Audit und "Gemeldete Konversationen" — beide zeigen auch Voice- bzw. Mail-Sessions — sowie der Mail-Agent und der produktive KI-Telefonassistent.',
  },
  customerAccount: {
    label: 'Kundenkonto (Stempelkarte & Glücksrad)',
    description:
      'Kundenkonto im Customer-Frontend — Anmeldung per Magic-Link und persönlichem Zugangslink, Buchungs- und Gutscheinübersicht, Stempelkarte über alle Erlebniswelten und Glücksrad. Im Dashboard hängt die Konfigurationsansicht "Treueprogramm" (Route /loyaltyProgram, Recht loyalty:manage) daran.',
    stage: 'secretLive',
    since: '2026-08-28',
    owner: 'max@laserzone.de',
    pageRoutes: ['/loyaltyProgram'],
    partialRoutes: [],
    note: 'Geheim statt nur zukünftig: Das Konto soll fertig gefunden werden — die Stempelkarte ist beim ersten Öffnen bereits mit den bisherigen Buchungen gefüllt —, deshalb darf live nichts davon zu lesen sein, auch kein Teaser unter „Demnächst verfügbar". Das Backend gated mit CUSTOMER_ACCOUNT_ENABLED die gesamte /customer-account-API, die Konfigurations-Route und — wichtig — die automatische Auszahlung der Stempelkarte: Solange das Flag aus ist, geht auch keine Mail an einen Kunden raus, der die Karte vollmacht. Das Customer-Frontend registriert seine Konto-Routen mit VITE_CUSTOMER_ACCOUNT_ENABLED gar nicht erst, der Produktions-Build enthält den Code nicht. Jeder bestehende Online-Kunde hat bereits einen dauerhaften Zugangs-Token (Startup-Migration), damit der persönliche Link am Tag der Freischaltung für alle existiert — verschickt wurde nichts.',
  },
  competitorStatistics: {
    label: 'Konkurrenten-Statistik',
    description:
      'Auswertung der Verfügbarkeiten von Wettbewerbern — Menüeintrag "Konkurrenten-Statistik" und Route /competitors, Recht competitorAvailability:read.',
    stage: 'secretLive',
    since: '2026-09-02',
    owner: 'max@laserzone.de',
    pageRoutes: ['/competitors'],
    partialRoutes: [],
    note: 'Läuft in der Produktion und bleibt bewusst unerwähnt: Die Ansicht ist nicht für jeden Mitarbeiter gedacht, deshalb darf nichts darüber in einem Changelog, einem Guide oder einem Teaser stehen — auch nicht als Nebensatz in einem fremden Item. Wer sie sehen darf, entscheidet weiterhin allein das Recht competitorAvailability:read; das Flag schaltet die Ansicht nicht ab, es hält sie nur aus allem heraus, was live lesbar ist.',
  },
  staffCostAreaOverview: {
    label: 'Personalkosten (Gesamt / Bereiche)',
    description:
      'Unternehmensweite Personalkosten-Statistik inkl. aller Verwaltungs-/Office-Bereiche — Menüeintrag "Personalkosten (Gesamt / Bereiche)" und Route /areaStaffCostStatistics.',
    stage: 'upcoming',
    since: '2026-07-27',
    owner: 'max@laserzone.de',
    // Only the standalone overview is gated; the area-locked embedding inside the
    // IT statistic stays visible on live and therefore gets no notice.
    pageRoutes: ['/areaStaffCostStatistics'],
    partialRoutes: [],
    note: 'Vorerst versteckt, bis Bereichs-/Rollenzuordnung und hinterlegte Gehälter vollständig verifiziert sind. Betrifft nur die Gesamt-/Bereichsübersicht; die IT-Personalkosten (nur IT-Bereich) in der IT-Statistik bleiben sichtbar. Der Backend-Endpoint bleibt rechtebasiert geschützt und wird durch das Flag nicht verändert.',
  },
  qrTickets: {
    label: 'QR-Code-Tickets',
    description:
      'PDF-Eintrittstickets mit QR-Code — Button "Ticket herunterladen" in den Buchungsdetails und der globale Handscanner-Listener, der aus jeder Ansicht heraus einen gescannten Ticket-Code annimmt und eincheckt.',
    stage: 'upcoming',
    since: '2026-08-08',
    owner: 'max@laserzone.de',
    // Booking-details button plus a listener that is active in every view — no
    // route of its own, so no page banner.
    pageRoutes: [],
    partialRoutes: [],
    note: 'Vorerst versteckt, bis der Einlass-Prozess mit den Standorten abgestimmt ist. Das Backend blockt zusätzlich alle /api/v1/ticket/*-Endpoints, solange dort TICKETS_ENABLED=true fehlt, und hängt kein Ticket an Kundenmails — beide Seiten müssen zum Aktivieren gesetzt werden. Rechte (ticket:read, ticket:scan) und bereits vergebene Ticket-Codes bleiben erhalten.',
  },
  b2bStatistics: {
    label: 'B2B-Statistik',
    description:
      'Einbuchungen des B2B-Teams gegen dessen Personalkosten — Menüeintrag "B2B-Statistik" und Route /b2bStatistics.',
    stage: 'upcoming',
    since: '2026-08-08',
    owner: 'max@laserzone.de',
    pageRoutes: ['/b2bStatistics'],
    partialRoutes: [],
    note: 'Vorerst nur auf Staging, aus denselben Gründen wie die Personalkosten-Gesamtübersicht: Die Zahlen hängen an der Rollen-/Bereichszuordnung und den hinterlegten Stundenlöhnen und sind noch nicht verifiziert. Der Backend-Endpoint bleibt rechtebasiert geschützt (statistic:read / :staffCost / :b2b) und wird durch das Flag nicht verändert.',
  },
  airkey: {
    label: 'AirKey Zutrittsverwaltung',
    description:
      'EVVA-AirKey-Verwaltung — Menüeintrag "AirKey", Routen /airkey und /airkey/mappings sowie die AirKey-Aktionen in Benutzerverwaltung und Mitarbeiterstatus.',
    stage: 'upcoming',
    since: '2026-08-03',
    owner: 'max@laserzone.de',
    pageRoutes: ['/airkey'],
    partialRoutes: ['/userManagement', '/employeeStatus'],
    note: 'Deaktiviert, weil das Feature im laufenden Betrieb Probleme macht. Code, Rechte und bereits erfasste AirKey-Daten bleiben erhalten. Das Backend blockt zusätzlich alle /api/v1/airkey/*-Endpoints, solange dort AIRKEY_ENABLED=true fehlt — beide Seiten müssen zum Reaktivieren gesetzt werden.',
  },
  pizzaTreatment: {
    label: 'Pizzaliste (Düsseldorf)',
    description:
      'Pizzaliste/Vorbestellung-Tab in der Küchen-Ansicht inkl. Anruf-Aufgaben (Kindergeburtstage ohne Pizza) und Lieferanten-Übergabe für Standorte mit isSpecialPizzaTreatment.',
    // Live since release 8.0 — the tab only appears for branches with
    // isSpecialPizzaTreatment anyway; the backend needs PIZZA_TREATMENT_ENABLED=true.
    stage: 'live',
    since: '2026-08-10',
    owner: 'max@laserzone.de',
    // Extra tab inside the kitchen view, which itself is live.
    pageRoutes: [],
    partialRoutes: ['/kitchen'],
    note: 'Das Backend gated die Pizza-Vorbestellungs-Endpunkte zusätzlich mit PIZZA_TREATMENT_ENABLED (503 PIZZA_TREATMENT_DISABLED); Staging an, Live aus — beide Seiten müssen zum Freischalten gesetzt sein.',
  },
};

// camelCase flag key -> env var suffix, e.g. staffCostAreaOverview ->
// STAFF_COST_AREA_OVERVIEW.
const toEnvSuffix = key => key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();

// True while the bundle was built for the staging environment. Kept as a
// module-level constant on purpose: Vite inlines `import.meta.env` as an object
// literal, and directly after a concise arrow body (`() => import.meta.env…`)
// that literal would be parsed as a function block and break the build.
const IS_STAGING_BUILD = import.meta.env?.VITE_IS_STAGING === 'true';

/**
 * Whether this bundle was built for staging. Use it for tooling that must never
 * exist on live — a test run that really sends mail, a debug view. It is a
 * build-time constant, so such a control is not merely hidden on live but
 * absent from the bundle. The matching endpoints refuse outside staging on
 * their own as well; this only keeps the button from being offered.
 */
export const isStagingBuild = () => IS_STAGING_BUILD;

// Whether a gated feature is currently enabled. An env override always wins; a
// postponed feature stays off even on staging; staging shows every other
// registered flag; otherwise the registry stage decides. Unknown keys are
// treated as disabled everywhere — including staging — so a typo can never
// silently expose something.
export const isFeatureEnabled = key => {
  const flag = FEATURE_FLAGS[key];
  if (!flag) return false;
  const envValue = import.meta.env?.[`VITE_FEATURE_${toEnvSuffix(key)}`];
  if (envValue === 'true') return true;
  if (envValue === 'false') return false;
  // Checked before the staging rule on purpose: a postponed project has nothing
  // to review, so it comes off the test environment too. Only the explicit env
  // override above brings it back.
  if (flag.stage === 'postponed') return false;
  if (IS_STAGING_BUILD) return true;
  // 'secretLive' is on in production like 'live' — the two differ only in what may
  // be said about them, never in whether they run.
  return flag.stage === 'live' || flag.stage === 'secretLive';
};

// A feature's declared state. An unknown key resolves to 'secret' on purpose —
// the most restrictive answer, so a typo can never make something look
// announceable.
export const featureStage = key => FEATURE_FLAGS[key]?.stage ?? 'secret';

// Whether nothing about this feature may be shown here. True while the feature is
// secret, stagingOnly or postponed AND its flag is off — on staging a secret feature is
// ordinary, a postponed one stays hidden there as well. Use this wherever an off
// feature would otherwise still leave a trace (a teaser, a disabled menu entry,
// an explanatory note).
export const isFeatureSecret = key => {
  // Asked before the switch: a secretly live feature is on AND unmentionable, so it
  // stays hidden precisely while it works.
  if (featureStage(key) === 'secretLive') return true;
  if (isFeatureEnabled(key)) return false;
  const stage = featureStage(key);
  return stage === 'secret' || stage === 'postponed' || stage === 'stagingOnly';
};

// All flags that are currently visible although they would be off on live —
// i.e. everything the current environment shows on top of production. On live
// this is always empty, because no flag resolves to enabled while its stage is
// not 'live'. Each entry is the registry entry plus its `key`.
export const getActiveFeatureFlags = () =>
  Object.entries(FEATURE_FLAGS)
    // 'secretLive' runs everywhere, so it is not something this environment shows on
    // top of production — and naming it in the banner would announce it.
    .filter(
      ([key, flag]) => isFeatureEnabled(key) && flag.stage !== 'live' && flag.stage !== 'secretLive'
    )
    .map(([key, flag]) => ({ key, ...flag }));

const normalizePath = pathname => {
  const trimmed = String(pathname || '/').trim();
  if (trimmed.length > 1 && trimmed.endsWith('/')) return trimmed.slice(0, -1);
  return trimmed || '/';
};

// A route matches when it is the path itself or one of its parent segments, so
// '/airkey' also covers '/airkey/mappings'.
const routeMatches = (pathname, route) => {
  const path = normalizePath(pathname);
  const candidate = normalizePath(route);
  return path === candidate || path.startsWith(`${candidate}/`);
};

// Splits the active flags into the ones that make the given page exist at all
// and the ones that only add a part to an otherwise live page. Flags whose
// surface has no route (modal buttons, global listeners) appear in neither.
export const getFeatureFlagsForPath = pathname => {
  const active = getActiveFeatureFlags();
  const matches = (flag, field) =>
    Array.isArray(flag[field]) && flag[field].some(route => routeMatches(pathname, route));

  return {
    pageFlags: active.filter(flag => matches(flag, 'pageRoutes')),
    partialFlags: active.filter(
      flag => !matches(flag, 'pageRoutes') && matches(flag, 'partialRoutes')
    ),
  };
};
